import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  Box,
  Burger,
  Drawer,
  Stack,
  NavLink as MantineNavLink,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconLogout, IconUser, IconLock } from '@tabler/icons-react';
import { useAuthStore } from '../../stores/auth';
import { SafeLoadIcon } from '../shared';

// Navigation items for admin
const navItems = [
  { path: '/', label: 'Dashboard' },
  { path: '/licenses', label: 'Licenses' },
  { path: '/customers', label: 'Customers' },
  { path: '/activations', label: 'Activations' },
  { path: '/products', label: 'Products' },
  { path: '/users', label: 'Users', adminOnly: true },
  { path: '/audit-log', label: 'Audit Log', adminOnly: true },
];

export function AdminLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get user initials for avatar
  const userInitials = user?.name?.split(' ').map(n => n[0]).join('') || 'U';
  const userName = user?.name || 'User';
  const userRole = user?.role === 'admin' ? 'Administrator' : 'Team Member';

  // Handle mobile nav link click
  const handleMobileNavClick = (path: string) => {
    navigate(path);
    closeMobileNav();
  };

  // Check if current path matches nav item
  const isActiveRoute = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Filter nav items based on user role
  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    return true;
  });

  return (
    <AppShell header={{ height: 60 }} padding={0}>
      {/* Header */}
      <AppShell.Header style={{ backgroundColor: '#1D252D' }}>
        <Group h="100%" px="md" justify="space-between">
          {/* Left: Burger (mobile), Logo and Navigation */}
          <Group gap="md">
            {/* Mobile burger menu */}
            <Burger
              opened={mobileNavOpened}
              onClick={toggleMobileNav}
              hiddenFrom="lg"
              size="sm"
              color="white"
            />

            <Group
              gap="xs"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              <SafeLoadIcon size={28} />
              <Text fw={600} size="lg" c="white" visibleFrom="xs">
                SafeLoad
              </Text>
            </Group>

            {/* Navigation Tabs - hidden on mobile/tablet */}
            <Group gap={4} visibleFrom="lg" ml={20}>
              {filteredNavItems.map(item => (
                <NavLink key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                  {({ isActive }) => (
                    <UnstyledButton
                      px="md"
                      py={6}
                      style={{
                        borderRadius: 4,
                        color: isActive ? 'white' : 'rgba(255,255,255,0.7)',
                        backgroundColor: isActive ? '#C41E3A' : 'transparent',
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {item.label}
                    </UnstyledButton>
                  )}
                </NavLink>
              ))}
            </Group>
          </Group>

          {/* Right: User Menu */}
          <Group gap="md">
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton
                  style={{
                    padding: '4px 8px',
                    borderRadius: 4,
                  }}
                >
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="leicaRed">
                      {userInitials}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={500} c="white" lh={1.2}>
                        {userName}
                      </Text>
                      <Text size="xs" c="rgba(255,255,255,0.7)" lh={1.2}>
                        {userRole}
                      </Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                <Menu.Item
                  leftSection={<IconUser size={14} />}
                  onClick={() => navigate('/profile')}
                >
                  Profile
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconLock size={14} />}
                  onClick={() => navigate('/settings')}
                >
                  Update Password
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconLogout size={14} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={closeMobileNav}
        size="xs"
        padding="md"
        title={
          <Group gap="xs">
            <SafeLoadIcon size={24} />
            <Text fw={600}>SafeLoad</Text>
          </Group>
        }
        hiddenFrom="lg"
        zIndex={1000}
      >
        <Stack gap="xs">
          {filteredNavItems.map(item => (
            <MantineNavLink
              key={item.path}
              label={item.label}
              active={isActiveRoute(item.path)}
              onClick={() => handleMobileNavClick(item.path)}
            />
          ))}
        </Stack>
      </Drawer>

      {/* Main Content */}
      <AppShell.Main style={{ backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 60px)' }}>
        <Box p="md">
          <Outlet />
        </Box>
      </AppShell.Main>
    </AppShell>
  );
}

export default AdminLayout;
